import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { League } from '../../models/leagues/league.model';
import { LeagueDivision } from '../../models/leagues/league-division.model';
import { LeagueDivisionsFiltersRequest } from '../../models/leagues/requests/api-leagues-divisions-request.model';
import { ApiLeaguesService } from '../../services/leagues/api/api-leagues.service';
import { AlertsService } from '../../services/alerts.service';
import { ModalsService } from '../../services/modals.service';
import { DateService } from '../../services/date.service';
import { LeagueAddModalComponent } from './modals/league-add-modal/league-add-modal.component';
import { LeagueEditModalComponent } from './modals/league-edit-modal/league-edit-modal.component';
import { LeagueRewardsModalComponent } from './modals/league-rewards-modal/league-rewards-modal.component';
import { LeagueDivisionUsersModalComponent } from './modals/league-division-users-modal/league-division-users-modal.component';
import { ConfigurationsBlockComponent } from '../configurations/configurations-block/configurations-block.component';
import { ConfigurationDef } from '../configurations/configurations-block/configuration-def.model';

@Component({
  templateUrl: 'leagues.component.html',
  standalone: true,
  imports: [
    CommonModule, FormsModule, TooltipModule, CollapseModule, TabsModule,
    LeagueAddModalComponent, LeagueEditModalComponent,
    LeagueRewardsModalComponent, LeagueDivisionUsersModalComponent,
    ConfigurationsBlockComponent
  ]
})
export class LeaguesComponent implements OnInit {

  @ViewChild('leagueAddModal') leagueAddModal!: LeagueAddModalComponent;
  @ViewChild('leagueEditModal') leagueEditModal!: LeagueEditModalComponent;
  @ViewChild('leagueRewardsModal') leagueRewardsModal!: LeagueRewardsModalComponent;
  @ViewChild('leagueDivisionUsersModal') leagueDivisionUsersModal!: LeagueDivisionUsersModalComponent;

  readonly leagueConfigurations: ConfigurationDef[] = [
    { name: 'game_league_feature_active', label: 'Feature Active', type: 'bool' },
    { name: 'game_league_division_users_max_count', label: 'Division Users Max Count', type: 'number' },
    { name: 'game_league_division_games_min_count', label: 'Division Games Min Count', type: 'number' },
    { name: 'game_league_demote_limit_start', label: 'Demote Limit Start', type: 'number' },
    { name: 'game_league_promote_limit', label: 'Promote Limit', type: 'number' },
    { name: 'game_league_level_unlocked', label: 'Level Unlocked', type: 'number' },
    { name: 'leagues_clean_timeout', label: 'Clean Timeout', type: 'number' },
  ];

  // Leagues tab
  leagues: League[] = [];
  leaguesIsLoading: boolean = false;

  // Divisions tab
  divisions: LeagueDivision[] = [];
  divisionsIsLoading: boolean = false;
  divisionsTotalResults: number = 0;
  readonly divisionsResultsPerPage: number = 25;
  divisionsIsFilterOpen: boolean = false;
  divisionsFilters: LeagueDivisionsFiltersRequest = new LeagueDivisionsFiltersRequest();
  divisionsEndDateMinFilter: string = '';
  divisionsEndDateMaxFilter: string = '';
  private static divisionsPageNumber: number = 1;

  get divisionsPageNumber(): number { return LeaguesComponent.divisionsPageNumber; }
  get divisionsTotalPages(): number { return Math.ceil(this.divisionsTotalResults / this.divisionsResultsPerPage); }

  constructor(
    public dateService: DateService,
    private apiLeaguesService: ApiLeaguesService,
    private alertsService: AlertsService,
    private modalsService: ModalsService
  ) {}

  ngOnInit(): void {
    this.refreshLeagues();
  }

  onSelectLeaguesTab(): void { this.refreshLeagues(); }

  onSelectDivisionsTab(): void { this.refreshDivisions(); }

  isDivisionInProgress(endDateTimestamp: number): boolean {
    return endDateTimestamp > Math.floor(Date.now() / 1000);
  }

  getLeagueTierById(id: number): string {
    const league = this.leagues.find(l => l.leagueId === id);
    return league ? String(league.tier) : (id ? String(id) : 'â€”');
  }

  getPageNumbers(current: number, total: number): (number | null)[] {
    const visibleSet = new Set<number>();
    for (let i = 1; i <= Math.min(2, total); i++) visibleSet.add(i);
    for (let i = Math.max(total - 1, 1); i <= total; i++) visibleSet.add(i);
    for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) visibleSet.add(i);
    const sorted = Array.from(visibleSet).sort((a, b) => a - b);
    const result: (number | null)[] = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push(null);
      result.push(sorted[i]);
    }
    return result;
  }

  // --- Divisions pagination & filters ---
  divisionsGoToPage(page: number): void { LeaguesComponent.divisionsPageNumber = page; this.refreshDivisions(); }
  onDivisionsPaginationPrevious(): void { LeaguesComponent.divisionsPageNumber--; this.refreshDivisions(); }
  onDivisionsPaginationNext(): void { LeaguesComponent.divisionsPageNumber++; this.refreshDivisions(); }

  onClickDivisionsFilter(): void {
    this.divisionsFilters.end_date_min_timestamp = this.divisionsEndDateMinFilter ? Math.floor(new Date(this.divisionsEndDateMinFilter).getTime() / 1000) : null;
    this.divisionsFilters.end_date_max_timestamp = this.divisionsEndDateMaxFilter ? Math.floor(new Date(this.divisionsEndDateMaxFilter).getTime() / 1000) : null;
    LeaguesComponent.divisionsPageNumber = 1;
    this.refreshDivisions();
  }

  onClickDivisionsResetFilters(): void {
    this.divisionsFilters = new LeagueDivisionsFiltersRequest();
    this.divisionsEndDateMinFilter = '';
    this.divisionsEndDateMaxFilter = '';
    LeaguesComponent.divisionsPageNumber = 1;
    this.refreshDivisions();
  }

  // --- League actions ---
  onClickAddLeague(): void {
    this.leagueAddModal.show(this.leagues, () => this.refreshLeagues());
  }

  onClickEditLeague(league: League): void {
    this.leagueEditModal.show(league, this.leagues, () => this.refreshLeagues());
  }

  onClickLeagueRewards(league: League): void {
    this.leagueRewardsModal.show(league.leagueId, league.name);
  }

  onClickDeleteLeague(league: League): void {
    this.modalsService.showConfirmModal(() => {
      this.apiLeaguesService.deleteLeague(league.leagueId).then(
        () => {
          this.alertsService.displayAlertSuccess('League deleted !');
          this.refreshLeagues();
        }, error => {
          this.alertsService.displayAlertError('Error deleting league : ' + error);
        }
      );
    });
  }

  // --- Division actions ---
  onClickDivisionUsers(division: LeagueDivision): void {
    this.leagueDivisionUsersModal.show(division.divisionId);
  }

  private refreshLeagues(): void {
    this.leaguesIsLoading = true;
    this.apiLeaguesService.getLeagues().then(
      response => {
        this.leaguesIsLoading = false;
        this.leagues = response.results;
      }, error => {
        this.leaguesIsLoading = false;
        this.alertsService.displayAlertError('Error loading leagues : ' + error);
      }
    );
  }

  private refreshDivisions(): void {
    this.divisionsIsLoading = true;
    this.apiLeaguesService.getLeaguesDivisions(LeaguesComponent.divisionsPageNumber, this.divisionsResultsPerPage, this.divisionsFilters).then(
      response => {
        this.divisionsIsLoading = false;
        this.divisions = response.results;
        this.divisionsTotalResults = response.resultsTotal;
      }, error => {
        this.divisionsIsLoading = false;
        this.alertsService.displayAlertError('Error loading divisions : ' + error);
      }
    );
  }
}
